import React, { useEffect, useRef } from 'react';

// Import delle icone delle materie più comuni (versione Deselected)
import matematicaIcon from '../ui/Materie/matematicaDeselected.svg';
import italianoIcon from '../ui/Materie/italianoDeselected.svg';
import ingleseIcon from '../ui/Materie/ingleseDeselected.svg';
import fisicaIcon from '../ui/Materie/fisicaDeselected.svg';
import chimicaIcon from '../ui/Materie/chimicaDeselected.svg';
import storiaIcon from '../ui/Materie/storiaDeselected.svg';
import filosofiaIcon from '../ui/Materie/filosofiaDeselected.svg';
import latinoIcon from '../ui/Materie/latinoDeselected.svg';
import grecoIcon from '../ui/Materie/grecoDeselected.svg';
import biologiaIcon from '../ui/Materie/biologiaDeselected.svg';
import informaticaIcon from '../ui/Materie/informaticaDeselected.svg';
import dirittoIcon from '../ui/Materie/dirittoDeselected.svg';
import economiaAziendaleIcon from '../ui/Materie/economiaAziendaleDeselected.svg';
import geografiaIcon from '../ui/Materie/geografiaDeselected.svg';
import franceseIcon from '../ui/Materie/franceseDeselected.svg';

interface Subject {
  name: string;
  icon: string;
}

const subjects: Subject[] = [
  { name: 'Matematica', icon: matematicaIcon },
  { name: 'Italiano', icon: italianoIcon },
  { name: 'Inglese', icon: ingleseIcon },
  { name: 'Fisica', icon: fisicaIcon },
  { name: 'Chimica', icon: chimicaIcon },
  { name: 'Storia', icon: storiaIcon },
  { name: 'Filosofia', icon: filosofiaIcon },
  { name: 'Latino', icon: latinoIcon },
  { name: 'Greco', icon: grecoIcon },
  { name: 'Biologia', icon: biologiaIcon },
  { name: 'Informatica', icon: informaticaIcon },
  { name: 'Diritto', icon: dirittoIcon },
  { name: 'Economia Aziendale', icon: economiaAziendaleIcon },
  { name: 'Geografia', icon: geografiaIcon },
  { name: 'Francese', icon: franceseIcon },
];

const SubjectsCarousel: React.FC = () => {
  // Moltiplichiamo l'array 5 volte per garantire sempre contenuto visibile
  const multipliedSubjects = Array(5).fill(subjects).flat();

  const containerRef = useRef<HTMLDivElement>(null);
  const animationFrameRef = useRef<number>();
  const translateXRef = useRef<number>(0);
  const lastTimeRef = useRef<number>(0);
  const singleSetWidthRef = useRef<number>(0);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Aspetta che le immagini siano caricate per calcolare la larghezza corretta
    const calculateWidth = () => {
      // Calcola la larghezza di un singolo set (1/5 del totale)
      const totalWidth = container.scrollWidth;
      singleSetWidthRef.current = totalWidth / 5;
    };

    // Calcola la larghezza dopo un breve delay per assicurarsi che le immagini siano caricate
    setTimeout(calculateWidth, 100);

    // Velocità di scorrimento in pixel al secondo (50px/s per velocità media)
    const speed = 50;

    // Funzione di animazione con requestAnimationFrame
    const animate = (currentTime: number) => {
      if (!lastTimeRef.current) {
        lastTimeRef.current = currentTime;
      }

      // Calcola il delta time in secondi, limitato a max 100ms per evitare salti
      const deltaTime = Math.min((currentTime - lastTimeRef.current) / 1000, 0.1);
      lastTimeRef.current = currentTime;

      // Aggiorna la posizione
      translateXRef.current += speed * deltaTime;

      // Reset seamless usando il modulo matematico
      if (singleSetWidthRef.current > 0) {
        translateXRef.current = translateXRef.current % singleSetWidthRef.current;
      }

      // Aggiorna direttamente il DOM senza causare re-render React
      if (container) {
        container.style.transform = `translateX(-${translateXRef.current}px)`;
      }

      // Continua l'animazione
      animationFrameRef.current = requestAnimationFrame(animate);
    };

    // Avvia l'animazione
    animationFrameRef.current = requestAnimationFrame(animate);

    // Cleanup: ferma l'animazione quando il componente viene smontato
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  return (
    <div className="relative w-full overflow-hidden py-4 md:py-8">
      {/* Gradient overlay per l'effetto fade agli estremi */}
      <div className="absolute inset-0 z-10 pointer-events-none">
        <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-fpcwhite to-transparent"></div>
        <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-fpcwhite to-transparent"></div>
      </div>

      {/* Container dello scroll infinito */}
      <div
        ref={containerRef}
        className="flex"
        style={{
          willChange: 'transform',
          width: 'fit-content',
        }}>
        {multipliedSubjects.map((subject, index) => (
          <div
            key={`${subject.name}-${index}`}
            className="flex-shrink-0 mx-4 md:mx-6 flex flex-col items-center justify-center">
            <img
              src={subject.icon}
              alt={subject.name}
              className="w-12 h-12 md:w-16 lg:w-20 md:h-16 lg:h-20 object-contain"
            />
            <span className="mt-1 md:mt-2 text-xs md:text-sm text-gray-700 text-center whitespace-nowrap">{subject.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SubjectsCarousel;
